"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
} from "react";

export type ConsentStatus = "unknown" | "granted" | "denied";

const STORAGE_KEY = "language-plus-analytics-consent";
const CONSENT_EVENT = "language-plus-consent-change";

interface ConsentContextValue {
  status: ConsentStatus;
  grant: () => void;
  deny: () => void;
  /** Clears the stored choice so the banner reappears (e.g. "manage cookies"). */
  reset: () => void;
}

const ConsentContext = createContext<ConsentContextValue | null>(null);

function isConsentStatus(value: string | null): value is Exclude<ConsentStatus, "unknown"> {
  return value === "granted" || value === "denied";
}

function readStatus(): ConsentStatus {
  if (typeof window === "undefined") {
    return "unknown";
  }

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isConsentStatus(stored) ? stored : "unknown";
  } catch {
    return "unknown";
  }
}

function writeStatus(status: Exclude<ConsentStatus, "unknown"> | null) {
  try {
    if (status === null) {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, status);
    }
  } catch {
    // Consent still applies for the current session even if storage is unavailable.
  }

  window.dispatchEvent(new Event(CONSENT_EVENT));
}

function subscribe(onChange: () => void) {
  window.addEventListener(CONSENT_EVENT, onChange);
  window.addEventListener("storage", onChange);

  return () => {
    window.removeEventListener(CONSENT_EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
}

/**
 * Site-wide gate for non-essential tracking (currently: Firebase Analytics).
 * No analytics call fires until the visitor explicitly grants consent, and the
 * choice can be revoked at any time via `reset`, which reopens the banner.
 */
export function ConsentProvider({ children }: { children: ReactNode }) {
  // useSyncExternalStore keeps the server snapshot ("unknown") and first client
  // render identical, then resolves to the stored choice after hydration.
  const status = useSyncExternalStore(subscribe, readStatus, () => "unknown" as const);

  const grant = useCallback(() => writeStatus("granted"), []);
  const deny = useCallback(() => writeStatus("denied"), []);
  const reset = useCallback(() => writeStatus(null), []);

  const value = useMemo(() => ({ status, grant, deny, reset }), [status, grant, deny, reset]);

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent() {
  const context = useContext(ConsentContext);

  if (!context) {
    throw new Error("useConsent must be used within ConsentProvider");
  }

  return context;
}

/** Synchronous read for call sites (e.g. the analytics helper) that can't use hooks. */
export function hasAnalyticsConsent(): boolean {
  return readStatus() === "granted";
}
